import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ClientDeadlinesApi, {
  ClientDeadline,
  describeDaysLeft,
  formatDeadlineDate,
} from '../../api/ClientDeadlinesApi';

/**
 * "Your deadlines" on the client's case page (Lloyd, 6 Oct 2026: "each client sees their own
 * deadlines and due dates").
 *
 * Shows nothing at all when the case has no conditions, which is most cases before an offer is
 * accepted: an empty "Your deadlines" box would read as if something were missing.
 */
const STATE_CHIP: Record<ClientDeadline['state'], { color: 'error' | 'warning' | 'default' | 'success'; label?: string }> = {
  overdue: { color: 'error' },
  'due-soon': { color: 'warning' },
  upcoming: { color: 'default' },
  'no-date': { color: 'default', label: 'Date to be confirmed' },
  done: { color: 'success', label: 'Done' },
};

const ClientDeadlinesCard: React.FC<{ ticketGuid: string }> = ({ ticketGuid }) => {
  const [deadlines, setDeadlines] = useState<ClientDeadline[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ClientDeadlinesApi.list(ticketGuid)
      .then((list) => !cancelled && setDeadlines(list))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [ticketGuid]);

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        We could not load your deadlines just now. Please refresh, or call us on 210-210-1616.
      </Alert>
    );
  }
  if (!deadlines || deadlines.length === 0) return null;

  const outstanding = deadlines.filter((d) => d.state !== 'done').length;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" color="primary" gutterBottom>
        Your deadlines
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {outstanding > 0
          ? 'The court requires these before your case can be dismissed. Each must be done by its date.'
          : 'Everything the court required is done.'}
      </Typography>

      <Stack spacing={1.5}>
        {deadlines.map((d) => {
          const chip = STATE_CHIP[d.state];
          const done = d.state === 'done';
          return (
            <Box key={d.guid} display="flex" gap={1.5} alignItems="flex-start">
              <Box sx={{ pt: 0.25 }}>
                {done ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : d.state === 'overdue' ? (
                  <WarningAmberIcon color="error" fontSize="small" />
                ) : (
                  <EventIcon color="action" fontSize="small" />
                )}
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="body1"
                  sx={done ? { textDecoration: 'line-through', color: 'text.secondary' } : undefined}
                >
                  {d.sentence}
                </Typography>
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mt={0.5}>
                  {d.dueAt && (
                    <Typography variant="body2" color="text.secondary">
                      {done ? 'Was due' : 'Due'} {formatDeadlineDate(d.dueAt)}
                    </Typography>
                  )}
                  <Chip size="small" color={chip.color} label={chip.label ?? describeDaysLeft(d) ?? ''} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {deadlines.some((d) => d.state === 'overdue') && (
        <Alert severity="error" sx={{ mt: 2 }}>
          A deadline has passed. Please call us on 210-210-1616 as soon as you can.
        </Alert>
      )}
    </Paper>
  );
};

export default ClientDeadlinesCard;
